/**
 * WebSocket Service for real-time market data and order updates
 */

import { io } from 'socket.io-client';
import { store } from '../redux/store';
import {
  setSocket,
  setConnected,
  updateMarketData,
  updateDepthData,
} from '../redux/slices/marketSlice';
import { addOrderUpdate } from '../redux/slices/orderSlice';

const WS_URL = process.env.REACT_APP_WS_URL || 'http://localhost:5000';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
  }

  /**
   * Connect to WebSocket server
   */
  connect(userId, userSession) {
    if (this.socket && this.isConnected) {
      console.log('WebSocket already connected');
      return;
    }

    this.socket = io(WS_URL, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected:', this.socket.id);
      this.isConnected = true;
      store.dispatch(setConnected(true));
      store.dispatch(setSocket(this.socket));

      // Authenticate
      this.socket.emit('authenticate', {
        userId,
        userSession,
      });
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      this.isConnected = false;
      store.dispatch(setConnected(false));
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    // Market data update
    this.socket.on('market-update', (data) => {
      if (data.type === 'market_data' && data.data.t === 'tf') {
        store.dispatch(updateMarketData(data.data));
      } else if (data.type === 'market_depth' && data.data.t === 'df') {
        store.dispatch(updateDepthData(data.data));
      }
    });

    // Order update
    this.socket.on('order-update', (data) => {
      if (data.type === 'order_update' && data.data.t === 'om') {
        store.dispatch(addOrderUpdate(data.data));
      }
    });
  }

  /**
   * Subscribe to market data
   */
  subscribeMarketData(tokens) {
    if (this.socket && this.isConnected) {
      this.socket.emit('subscribe-market-data', tokens);
    }
  }

  /**
   * Unsubscribe from market data
   */
  unsubscribeMarketData(tokens) {
    if (this.socket && this.isConnected) {
      this.socket.emit('unsubscribe-market-data', tokens);
    }
  }

  /**
   * Subscribe to market depth
   */
  subscribeMarketDepth(tokens) {
    if (this.socket && this.isConnected) {
      this.socket.emit('subscribe-market-depth', tokens);
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      store.dispatch(setConnected(false));
      store.dispatch(setSocket(null));
    }
  }

  /**
   * Get socket instance
   */
  getSocket() {
    return this.socket;
  }
}

const wsService = new WebSocketService();
export default wsService;
